import { Formik } from 'formik';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';

import CardText from 'components/Card/Text';
import CardTitle from 'components/Card/Title';
import LoadBlur from 'components/LoadBlur';
import auth from 'lib/auth';

import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';

const ResetPassword = ({ loading, onLoading, onLogin, onStateChange }) => {
  const [error, setError] = useState(null);
  return (
    <Formik
      initialValues={useMemo(
        () => ({
          password: '',
        }),
        [],
      )}
      onSubmit={useCallback(
        async ({ password }, { setSubmitting }) => {
          onLoading(true);
          const { err, message, state } = await auth.resetPassword(password);
          if (err) {
            setError(message);
            onLoading(false);
          } else if (state) {
            onStateChange(state);
          } else {
            onLoading(false);
          }
          setSubmitting(false);
        },
        [
          onLoading,
          onStateChange,
        ],
      )}
      validationSchema={useMemo(
        () => Yup.object()
          .shape({
            password: Yup.string()
              .trim()
              .min(8, 'Password must be at least 8 characters long')
              .required('Password is required'),
          }),
        [],
      )}
    >
      {useCallback(
        ({
          dirty,
          errors,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          submitForm,
          touched,
          values,
        }) => {
          const busy = isSubmitting || loading;
          const canSubmit = !busy && dirty && isValid;
          return (
            <LoadBlur loading={isSubmitting}>
              <CardTitle title='Reset Password' />
              <CardText noTopPadding>
                <TextField
                  disabled={busy}
                  helperText={touched?.password && errors?.password}
                  error={touched?.password && errors?.password}
                  label='New Password'
                  type='password'
                  fullWidth
                  onBlur={() => setFieldTouched('password')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('password', value);
                  }}
                  value={values.password}
                />
              </CardText>
              <CardText noTopPadding>
                <em>
                  Passwords can be any length greater than 8 characters. You
                  may enter letters, numbers, spaces, symbols, 🗝 (emojis), or
                  entire even sentences. Just be sure to enter something you
                  can remember!
                </em>
              </CardText>
              <CardText noTopPadding>
                <SecondaryButton
                  label='Remember your password?'
                  onClick={onLogin}
                />
              </CardText>
              <PrimaryButton
                disabled={!canSubmit}
                error={error}
                icon={<CheckIcon />}
                label='Reset password'
                loading={busy}
                onClick={submitForm}
              />
            </LoadBlur>
          );
        },
        [
          error,
          loading,
          onLogin,
        ],
      )}
    </Formik>
  );
};

ResetPassword.propTypes = {
  loading: PropTypes.bool,
  onLoading: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
};

export default ResetPassword;
